import * as React from "react";
import { mergeClasses } from "@fluentui/react-components";
import { useSharedStyles } from "../immobilieContractStep/sharedStyles";
import { useTransportStyles } from "./sharedStyles";
import SubTable from "./SubTable";
import { getList, getRecord, getRows, getText } from "./values";
import type { TrField, TrValue, TrValues } from "./types";

/**
 * Zeichnet ein einzelnes Feld der Transport-Vertragsmaske als Karte:
 * Bezeichnung und Herkunfts-Abzeichen oben, die Eingabe in der Mitte, der
 * Sachbearbeiter-Hinweis aus Spalte G darunter.
 *
 * Welche Eingabe erscheint, entscheidet allein `field.type` — die Feldliste
 * in `sections.ts` bleibt damit reine Beschreibung und enthält kein Markup.
 */

/** Kurze Auswahllisten stehen als Pillen nebeneinander statt in einem Menü. */
const alsPillen = (options: string[]) =>
  options.length > 0 && options.length <= 4 && options.every((o) => o.length <= 30);

type Props = {
  field: TrField;
  values: TrValues;
  onChange: (key: string, value: TrValue | undefined) => void;
  /**
   * Werte aus der Transport-Analyse, nach Quelle-ID (z. B. „TR-D04").
   * Speisen die Anzeigefelder aus Block D.
   */
  analyseWerte?: Record<string, string>;
  disabled?: boolean;
};

const FieldRenderer: React.FC<Props> = ({ field, values, onChange, analyseWerte, disabled }) => {
  const s = useSharedStyles();
  const t = useTransportStyles();
  const analyse = field.type === "readout";
  const breit = field.type === "subtable" || field.type === "multi" || field.type === "textarea";

  const eingabe = () => {
    switch (field.type) {
      case "single": {
        const wert = getText(values, field.key);
        const options = field.options ?? [];
        if (alsPillen(options)) {
          return (
            <div className={s.pillRow} role="radiogroup" aria-label={field.label}>
              {options.map((o) => {
                const aktiv = wert === o;
                return (
                  <button
                    key={o}
                    type="button"
                    role="radio"
                    aria-checked={aktiv}
                    disabled={disabled}
                    className={mergeClasses(s.pillBtn, aktiv && s.pillBtnActive)}
                    // Erneutes Klicken der aktiven Option hebt die Wahl auf —
                    // sonst liesse sich ein versehentlich gesetztes Feld nicht
                    // mehr leeren.
                    onClick={() => onChange(field.key, aktiv ? "" : o)}
                  >
                    {o}
                  </button>
                );
              })}
            </div>
          );
        }
        return (
          <select
            className={s.input}
            value={wert}
            disabled={disabled}
            aria-label={field.label}
            onChange={(e) => onChange(field.key, e.target.value)}
          >
            <option value="">Bitte auswählen …</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        );
      }

      case "multi": {
        const gewaehlt = getList(values, field.key);
        return (
          <div className={s.pillRow} role="group" aria-label={field.label}>
            {(field.options ?? []).map((o) => {
              const aktiv = gewaehlt.includes(o);
              return (
                <button
                  key={o}
                  type="button"
                  aria-pressed={aktiv}
                  disabled={disabled}
                  className={mergeClasses(s.pillBtn, aktiv && s.pillBtnActive)}
                  onClick={() =>
                    onChange(
                      field.key,
                      aktiv ? gewaehlt.filter((g) => g !== o) : [...gewaehlt, o],
                    )
                  }
                >
                  {o}
                </button>
              );
            })}
          </div>
        );
      }

      case "eur":
        return (
          <div className={s.inputWithSuffix}>
            <input
              className={s.input}
              type="text"
              inputMode="decimal"
              value={getText(values, field.key)}
              disabled={disabled}
              placeholder={field.placeholder ?? "0,00"}
              aria-label={field.label}
              onChange={(e) => onChange(field.key, e.target.value)}
            />
            <span className={s.inlineSuffix}>EUR</span>
          </div>
        );

      case "text":
        return (
          <input
            className={s.input}
            type="text"
            value={getText(values, field.key)}
            disabled={disabled}
            placeholder={field.placeholder}
            aria-label={field.label}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        );

      case "textarea":
        return (
          <textarea
            className={s.textarea}
            value={getText(values, field.key)}
            disabled={disabled}
            placeholder={field.placeholder}
            aria-label={field.label}
            onChange={(e) => onChange(field.key, e.target.value)}
          />
        );

      case "combo":
      case "eurSelect": {
        const rec = getRecord(values, field.key);
        const parts = field.parts ?? [];
        const art = String(rec.art ?? "");
        /**
         * Beim Selbstbehalt bestimmt die gewählte Art, welche Werte überhaupt
         * gefragt sind: „kein Selbstbehalt" braucht keinen Betrag, ein fester
         * keinen Prozentsatz. Ohne diese Regel stünden immer alle drei Felder
         * da und luden zu widersprüchlichen Eingaben ein.
         */
        const zeigeTeil = (key: string) => {
          if (field.type !== "combo" || art === "") return true;
          if (art.startsWith("kein")) return key === "art";
          if (art.startsWith("fester")) return key !== "prozent";
          if (art.startsWith("prozentualer")) return key !== "eur";
          return true;
        };
        const sichtbar = parts.filter((p) => zeigeTeil(p.key));
        return (
          <div className={sichtbar.length > 2 ? s.threeCol : s.twoCol}>
            {sichtbar.map((p) => {
              const wert = String(rec[p.key] ?? "");
              const setze = (v: string) => onChange(field.key, { ...rec, [p.key]: v });
              return (
                <div key={p.key} className={s.field}>
                  <span className={s.label}>{p.label}</span>
                  {p.type === "select" ? (
                    <select
                      className={s.input}
                      value={wert}
                      disabled={disabled}
                      aria-label={`${field.label} — ${p.label}`}
                      onChange={(e) => setze(e.target.value)}
                    >
                      <option value="">Bitte auswählen …</option>
                      {(p.options ?? []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className={s.inputWithSuffix}>
                      <input
                        className={s.input}
                        type="text"
                        inputMode={p.type === "eur" || p.type === "percent" ? "decimal" : undefined}
                        value={wert}
                        disabled={disabled}
                        placeholder={p.placeholder ?? (p.type === "eur" ? "0,00" : undefined)}
                        aria-label={`${field.label} — ${p.label}`}
                        onChange={(e) => setze(e.target.value)}
                      />
                      {p.type === "eur" && <span className={s.inlineSuffix}>EUR</span>}
                      {p.type === "percent" && <span className={s.inlineSuffix}>%</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      }

      case "subtable":
        if (!field.subtable) return null;
        return (
          <SubTable
            spec={field.subtable}
            rows={getRows(values, field.key)}
            fieldId={field.id}
            disabled={disabled}
            onChange={(rows) => onChange(field.key, rows)}
          />
        );

      case "readout": {
        const wert = field.source ? (analyseWerte?.[field.source] ?? "") : "";
        return (
          <div className={mergeClasses(t.readout, wert === "" && t.readoutLeer)}>
            {wert === "" ? "Noch nicht aus der Analyse übernommen" : wert}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className={mergeClasses(t.fieldCard, breit && t.fieldCardWide)}>
      <div className={t.fieldTop}>
        <div style={{ minWidth: 0 }}>
          <div className={t.fieldLabel}>{field.label}</div>
          {field.feld && <div className={t.fieldSub}>{field.feld}</div>}
        </div>
        <span className={analyse ? t.badgeAnalyse : t.badgeVertrag}>
          {analyse ? "Analyse" : "Vertrag"}
        </span>
      </div>
      {eingabe()}
      {(field.hint || field.check) && (
        <div className={t.fieldNote}>
          {field.hint}
          {field.hint && field.check ? " " : ""}
          {field.check}
        </div>
      )}
      <div className={t.fieldId}>
        {field.id}
        {field.source ? ` · ${field.source}` : ""}
      </div>
    </div>
  );
};

export default FieldRenderer;
